import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Flex, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { Guard } from 'src/features/session/guard'
import { PERMISSIONS } from 'src/features/session/constants'
import {
  FilterMonitoring,
  TableMonitoring,
} from 'src/pages/billing/paymentTrafficMonitoring/components'
import { ValueFormFilter } from 'src/pages/billing/paymentTrafficMonitoring/types'
import { PERIOD_VALUES } from 'src/pages/billing/paymentTrafficMonitoring/const'
import {
  getMonitoringListCSVFx,
  loadMonitoringList,
  resetMonitoringList,
} from 'src/pages/billing/paymentTrafficMonitoring/model'

import dayjs from 'dayjs'
import { useUnit } from 'effector-react'

export const PaymentTrafficMonitoring = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const loadingCSV = useUnit(getMonitoringListCSVFx.pending)
  const { Title } = Typography

  const preset = searchParams.get('preset')
  const direction = searchParams.get('direction')
  const dateFrom = searchParams.get('dateFrom')
  const dateTo = searchParams.get('dateTo')

  const getInitialValues = (): ValueFormFilter => {
    const isDatePicker = Boolean(dateFrom && dateTo)

    return {
      direction: direction ?? undefined,
      periodDatePicker: isDatePicker
        ? [dayjs(dateFrom), dayjs(dateTo)]
        : undefined,
      preset: isDatePicker
        ? 'date_range'
        : preset ?? PERIOD_VALUES.LAST_30_MINUTES,
    }
  }

  const getFilters = (): Record<string, string | string[]> => {
    const filters: Record<string, string | string[]> = {}
    if (preset) {
      filters['preset'] = preset
    }
    if (direction) {
      filters['direction'] = direction
    }
    if (dateFrom) {
      filters['dateFrom'] = dateFrom
    }
    if (dateTo) {
      filters['dateTo'] = dateTo
    }

    const countries = searchParams.get('filterField_country')
    if (countries) {
      filters['country'] = countries.split(',')
    }

    const gateway = searchParams.get('searchField_gatewayId')
    if (gateway) {
      filters['gateway'] = gateway
    }

    const ps = searchParams.get('filterField_ps')
    if (ps) {
      filters['ps'] = ps.split(',')
    }

    const pendingAmountUsdFrom = searchParams.get('pendingAmountUsdFrom')
    if (pendingAmountUsdFrom) {
      filters['pendingAmountUsdFrom'] = pendingAmountUsdFrom
    }

    const pendingAmountUsdTo = searchParams.get('pendingAmountUsdTo')
    if (pendingAmountUsdTo) {
      filters['pendingAmountUsdTo'] = pendingAmountUsdTo
    }

     const vendorErrorPctFrom = searchParams.get('vendorErrorPctFrom')
     if (vendorErrorPctFrom) {
       filters['vendorErrorPctFrom'] = vendorErrorPctFrom
     }

     const vendorErrorPctTo = searchParams.get('vendorErrorPctTo')
     if (vendorErrorPctTo) {
       filters['vendorErrorPctTo'] = vendorErrorPctTo
     }

    const status = searchParams.get('filterField_status')
    if (status) {
      filters['status'] = status
    }

    return filters
  }

  const resetSearchParams = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.delete('direction')
      params.delete('dateTo')
      params.delete('dateFrom')
      params.delete('pendingAmountUsdFrom')
      params.delete('pendingAmountUsdTo')
      params.delete('status')
      params.set('preset', PERIOD_VALUES.LAST_30_MINUTES)
      return params
    })
  }

  const handleDownloadCSV = async () => {
    try {
      const { body } = await getMonitoringListCSVFx({
        query: {
          order: searchParams.get('sorterField') ?? '-status',
          page: searchParams.get('page') ?? '1',
          limit: searchParams.get('limit') ?? '50',
          where: JSON.stringify(getFilters() ?? {}),
        },
      })

      const today = dayjs().format('DD_MM_YYYY')

      const csvContent = body.items
        .map((item) => Object.values(item).join(','))
        .join('\n')

      const csvCode =
        'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

      const link = document.createElement('a')

      link.href = csvCode
      link.download = `traffic_monitoring_${today}.csv`
      link.target = '_blank'

      link.click()
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    resetMonitoringList()
    let timer: ReturnType<typeof setTimeout>

    const load = () => {
      loadMonitoringList({
        limit: Number(searchParams.get('limit')) || 50,
        page: Number(searchParams.get('page')) || 1,
        order: searchParams.get('sorterField') ?? '-status',
        filters: getFilters(),
      })

      timer = setTimeout(load, 60_000)
    }

    load()

    return () => {
      clearTimeout(timer)
    }
  }, [searchParams])

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center">
        <Title level={1} style={{ marginBottom: 0 }}>
          Payment traffic monitoring
        </Title>
        <Guard
          permission={PERMISSIONS.VIEW_PAYMENT_TRAFFIC_REPORT}
          mode="permission"
        >
          <Button
            loading={loadingCSV}
            size="large"
            icon={<DownloadOutlined />}
            onClick={handleDownloadCSV}
          >
            CSV
          </Button>
        </Guard>
      </Flex>

      <FilterMonitoring
        setSearchParams={setSearchParams}
        initialValues={getInitialValues()}
        resetSearchParams={resetSearchParams}
      />
      <TableMonitoring
        searchParams={searchParams}
        setSearchParams={setSearchParams}
      />
    </Flex>
  )
}
